import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Building2, Users, Star, Settings2,
  Sparkles, MessageSquareText, Code2, Compass,
  Bot, Zap, Brain, ShieldCheck,
  Video, Building, Laptop, Layers,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Building2, value: "250+", label: "Enterprise Clients", desc: "Trusted by organizations across industries for enterprise AI, Cloud and Digital Transformation learning." },
  { icon: Users, value: "4000+", label: "Professionals Upskilled", desc: "Helping professionals build practical AI capabilities with instructor-led learning." },
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Customized learning journeys delivering measurable business impact." },
  { icon: Settings2, value: "100%", label: "Customized Learning Solutions", desc: "Training programs aligned with your business goals, technology stack and workforce needs." }
];

const LEARNING_OUTCOMES = [
  { icon: Sparkles, title: "Generative AI Foundations", desc: "Develop a strong understanding of Generative AI concepts, foundation models, prompt design, and enterprise use cases to confidently adopt AI across business functions." },
  { icon: MessageSquareText, title: "Prompt Engineering", desc: "Learn advanced prompting techniques to generate accurate, context-aware responses using ChatGPT, Claude, Gemini, and other Large Language Models (LLMs)." },
  { icon: Code2, title: "Developing Generative AI Applications", desc: "Create enterprise-ready AI applications using APIs, Retrieval-Augmented Generation (RAG), vector databases, embeddings, and modern AI frameworks." },
  { icon: Compass, title: "AI Leadership & Strategy", desc: "Explore AI governance, Responsible AI, business adoption strategies, and implementation roadmaps to successfully lead enterprise AI initiatives." },
  { icon: Bot, title: "AI-Powered Software Development", desc: "Improve software development using GitHub Copilot, ChatGPT, and AI coding assistants for code generation, debugging, testing, documentation, and optimization." },
  { icon: Zap, title: "AI Automation & Productivity", desc: "Automate repetitive business and engineering tasks using AI-powered workflows that improve operational efficiency, collaboration, and decision-making." },
  { icon: Brain, title: "Large Language Models (LLMs)", desc: "Understand transformer architecture, GPT models, Llama, embeddings, fine-tuning, prompt optimization, and enterprise deployment of Large Language Models." },
  { icon: ShieldCheck, title: "Responsible AI & Enterprise Governance", desc: "Implement secure, ethical, and compliant AI practices by addressing governance, privacy, model evaluation, risk management, and enterprise AI policies." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive instructor-led online sessions with labs, projects and enterprise use cases." },
  { icon: Building, title: "Classroom Bootcamps", desc: "Hands-on corporate workshops delivered at your office or our training centers." },
  { icon: Laptop, title: "Self-paced Learning", desc: "Access learning resources, recordings, assessments and practice labs anytime." },
  { icon: Layers, title: "Blended Learning", desc: "Combine instructor-led sessions, self-learning and capstone projects for maximum adoption." }
];

const BY_THE_NUMBERS = [
  { value: "250+", label: "Enterprise Clients" },
  { value: "200+", label: "Corporate Learning Programs" },
  { value: "500+", label: "Instructor-Led Workshops" },
  { value: "100+", label: "Enterprise Trainers" }
];

const GenAIPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE AI LEARNING SOLUTIONS"
        title="Enterprise Generative AI Course for Future-Ready Teams"
        subtitle="Transform your workforce with Skillsgain's enterprise-focused Generative AI Course designed for business leaders, developers, data professionals, and innovation teams. Master Prompt Engineering, Large Language Models (LLMs), AI Automation, Transformer Architecture, Natural Language Processing (NLP), and modern AI Image Generator tools through instructor-led, hands-on learning."
      />

      {/* Enterprise Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">ENTERPRISE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Measurable business outcomes powered by AI training</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Generative AI Learning Outcomes */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">GENERATIVE AI LEARNING OUTCOMES</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Build practical AI capabilities that drive business innovation</h2>
            <p className="mt-3 text-gray-600">Develop enterprise-ready AI skills through hands-on projects, real-world use cases, and instructor-led workshops.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Flexible Enterprise Training */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">FLEXIBLE ENTERPRISE TRAINING</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Choose the delivery model that fits your workforce</h2>
              <p className="mt-3 text-gray-600">Whether you're training 20 employees or 20,000, Skillsgain delivers flexible enterprise learning experiences globally.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + testimonial */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#4F9622] mb-4"/>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain enabled our engineering teams to adopt Generative AI rapidly through customized Prompt Engineering and AI Automation training. Within weeks, our teams began integrating AI into daily workflows, improving productivity and accelerating innovation.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR ENTERPRISE TEAMS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Ready to Build AI-Ready Teams?</h2>
                <p className="mt-3 text-gray-300">Empower your workforce with Skillsgain's enterprise Generative AI Course featuring Prompt Engineering, Large Language Models, Transformer Architecture, Natural Language Processing, AI Automation, and AI Image Generator technologies.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our AI Expert</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default GenAIPage;
